import { useTranslation } from 'react-i18next';
import type { Currency, ItemPacking, ItemType, VehicleTypes } from '@hero/schema';
import { CONFIG } from '@hero/core';
import { useIsLogistiConnected } from '@hero/hooks-shared';
import { PencilIcon } from '@hero/web-icons';
import { Input } from '../../../ui/Input/Input';
import { VehicleTypeMultiSelector } from '../../Vehicle/VehicleTypeMultiSelector';
import { OrderItemTypeSelect } from '../Item/OrderItemTypeSelect';
import { OrderPackingTypeSelect } from '../Item/OrderPackingTypeSelect';
import { OrderTemperatureSelector } from '../Temperature/OrderTemperatureSelector';
import { WeightEdit } from '../../../app/Weight/WeightEdit';
import { PriceEdit } from '../../../app/Price/PriceEdit';
import { SelectAuto, type SelectAutoOption } from '../../../app/Select/SelectAuto';
import { PickupDateDisplay } from '../PickupDate/PickupDateDisplay';

/**
 * Vehicle types whose `Spec.isChilled` flag is `true` in the shared
 * core vehicle config — temperature is only a meaningful question
 * when at least one of those is picked.
 */
const CHILLED_VEHICLE_TYPES: ReadonlySet<VehicleTypes> = new Set(
  CONFIG.vehicles.types.filter(spec => spec.isChilled).map(spec => spec.id),
);

const hasChilledVehicle = (vehicles: VehicleTypes[]) =>
  vehicles.some(v => CHILLED_VEHICLE_TYPES.has(v));

export interface LoadDetailsFieldsValue {
  vehicleTypes: VehicleTypes[];
  temperatureType: string | undefined;
  itemType: ItemType | undefined;
  packing: ItemPacking | undefined;
  quantity: number;
  itemDescription: string;
  /** Weight expressed in `weightUnit`. */
  weight: number | undefined;
  itemCost: number | undefined;
  /** POD copy preference — only rendered when {@link showPodSettings} is on. */
  podSettings?: 'pod_image' | 'return_original_pod';
  /** Pickup window — only used when `showPickupDate` is `true`. */
  pickupFrom?: Date;
  pickupTo?: Date;
}

export interface LoadDetailsFieldsProps {
  value: LoadDetailsFieldsValue;
  onChange: (patch: Partial<LoadDetailsFieldsValue>) => void;
  /**
   * When `true`, render the pickup-date row at the top of the form
   * (used by the order-drawer edit-load surface). The publish-page
   * passes `false` because pickup is its own dedicated step.
   */
  showPickupDate?: boolean;
  /** Click target for the pickup-date row when {@link showPickupDate} is on. */
  onOpenPickupModal?: () => void;
  /**
   * Display + storage unit for the weight input. The publish flow
   * uses `'ton'` for parity with the order drawer; legacy callers
   * may pass `'kg'`.
   */
  weightUnit: 'kg' | 'ton';
  /** When `true`, render the POD copy preference row (publish flow). */
  showPodSettings?: boolean;
  /**
   * Currency used for the declared-value `<PriceEdit />`. Defaults to
   * `SAR` so callers don't have to thread it through unless they
   * specifically need a non-default currency.
   */
  currency?: Currency;
}

/**
 * Shared load-detail field block used by both:
 *   - the order-drawer edit-load surface (`OrderLoadEditDrawer`) — with
 *     pickup row, weight in tons, save → API;
 *   - the publish-page load drawer (`LoadDetailsFormDrawer`) — without
 *     pickup row, weight in tons, writes to the publish store live.
 *
 * Keeping every selector + label here means the two surfaces drift
 * in lockstep. Pickup is opt-in via {@link showPickupDate} so the
 * publish flow never grows a pickup field by accident.
 */
export function LoadDetailsFields({
  value,
  onChange,
  showPickupDate = false,
  onOpenPickupModal,
  weightUnit,
  currency = 'SAR',
  showPodSettings = false,
}: LoadDetailsFieldsProps) {
  const { t } = useTranslation(['m-order', 'c-main', 'c-price']);

  const showTemperature = hasChilledVehicle(value.vehicleTypes);

  // The field reads the org's own state rather than taking a prop: every
  // caller would otherwise have to thread the same hook through, and one that
  // forgot would silently let a Logisti org publish a load Bayan rejects.
  const isItemCostRequired = useIsLogistiConnected();
  const isItemCostMissing = isItemCostRequired && value.itemCost == null;

  return (
    <div className="space-y-4">
      {showPickupDate && (
        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">
            {t('m-order:drawer.load_edit.pickup_label', { defaultValue: 'Pickup' })}
          </label>
          <div className="relative">
            <PickupDateDisplay
              variant="button"
              pickupDate={
                value.pickupFrom && value.pickupTo
                  ? { from: value.pickupFrom, to: value.pickupTo }
                  : null
              }
              onClick={onOpenPickupModal}
              className="pe-12"
            />
            <span className="absolute end-3 top-1/2 -translate-y-1/2 inline-flex size-7 items-center justify-center rounded-full bg-gray-100 text-gray-600 pointer-events-none">
              <PencilIcon className="size-3.5" />
            </span>
          </div>
        </div>
      )}

      <div className="space-y-1">
        <label className="text-sm text-muted-foreground">
          {t('m-order:drawer.load_edit.vehicle_types_label', {
            defaultValue: 'Accepted vehicle types',
          })}
        </label>
        <VehicleTypeMultiSelector
          value={value.vehicleTypes}
          onChange={next => onChange({ vehicleTypes: next })}
          placeholder={t('m-order:drawer.load_edit.vehicle_types_placeholder', {
            defaultValue: 'Pick one or more',
          })}
        />
      </div>

      {showTemperature && (
        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">
            {t('m-order:drawer.load_edit.temperature_label', {
              defaultValue: 'Temperature',
            })}
          </label>
          <OrderTemperatureSelector
            value={value.temperatureType}
            onChange={next => onChange({ temperatureType: next })}
            placeholder={t('wizard.load.temperature_placeholder', {
              defaultValue: 'Dry / Chilled / Frozen',
            })}
          />
        </div>
      )}

      <div className="space-y-1">
        <label className="text-sm text-muted-foreground">
          {t('m-order:drawer.load_edit.item_name_label', {
            defaultValue: 'Item description',
          })}
        </label>
        <Input
          value={value.itemDescription}
          onChange={e => onChange({ itemDescription: e.target.value })}
          placeholder={t('m-order:drawer.load_edit.item_name_placeholder', {
            defaultValue: 'e.g. 12 boxes of medical supplies',
          })}
        />
      </div>

      <div className="space-y-1">
        <label className="text-sm text-muted-foreground">
          {t('m-order:drawer.load_edit.item_type_label', { defaultValue: 'Item type' })}
        </label>
        <OrderItemTypeSelect
          itemType={value.itemType}
          onMaterialChange={(v: ItemType) => onChange({ itemType: v })}
        />
      </div>

      <div className="space-y-1">
        <label className="text-sm text-muted-foreground">
          {t('m-order:drawer.load_edit.packing_label', {
            defaultValue: 'Packing & quantity',
          })}
        </label>
        <OrderPackingTypeSelect
          packing={value.packing}
          onPackingChange={(p: ItemPacking) => onChange({ packing: p })}
          counter={value.quantity}
          onCounterChange={n => onChange({ quantity: n })}
          showCounter
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">
            {t('m-order:drawer.load_edit.weight_label', { defaultValue: 'Weight' })}
          </label>
          <WeightEdit
            value={value.weight}
            onChange={n => onChange({ weight: n })}
            unit={weightUnit}
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">
            {t('c-price:declared_value', { defaultValue: 'Declared value' })}
            {isItemCostRequired && <span className="text-red-500 ms-0.5">*</span>}
          </label>
          <PriceEdit
            value={value.itemCost}
            onChange={n => onChange({ itemCost: n })}
            currency={currency}
          />
          {isItemCostMissing && (
            <p className="text-xs text-amber-600">
              {t('m-order:drawer.load_edit.item_cost_required', {
                defaultValue: 'Required — your organisation issues government waybills.',
              })}
            </p>
          )}
        </div>
      </div>

      {showPodSettings && (
        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">
            {t('m-order:drawer.load_edit.pod_settings_label', {
              defaultValue: 'Document copy returns',
            })}
          </label>
          <SelectAuto
            options={
              [
                {
                  value: 'pod_image',
                  label: t('m-order:drawer.load_edit.pod_settings_option_pod_image', {
                    defaultValue: 'Image only (no paper)',
                  }),
                },
                {
                  value: 'return_original_pod',
                  label: t('m-order:drawer.load_edit.pod_settings_option_return_original_pod', {
                    defaultValue: 'Return paper original',
                  }),
                },
              ] satisfies SelectAutoOption[]
            }
            value={value.podSettings ?? ''}
            onChange={next =>
              onChange({
                podSettings: (next || undefined) as
                  | 'pod_image'
                  | 'return_original_pod'
                  | undefined,
              })
            }
            placeholder={t('m-order:drawer.load_edit.pod_settings_placeholder', {
              defaultValue: 'Inherit organisation default',
            })}
          />
        </div>
      )}
    </div>
  );
}
